/**
 * Consolidated realtime subscription for company-scoped tables.
 * 
 * Currently handles shifts (open shifts) and time_entries (timesheet status
 * changes) via a SINGLE Supabase channel.
 * Attendance flags were removed from realtime in favor of refresh-on-focus
 * (useAttendanceFlags already has refetchOnWindowFocus: true + 2min staleTime)
 * to reduce WAL polling overhead from realtime.list_changes.
 *
 * FMEA Mitigations:
 * - F1: Only subscribes when companyId is available
 * - F2: Static channel name (no Date.now())
 * - F3: Cleanup before re-creation (HMR safety)
 * - F4: Proper cleanup on unmount
 * - F5: Open shift handler gated by offersEnabled ref (no unnecessary invalidations)
 * - F6: Company-scoped filters for tenant isolation
 * - F7: time_entries handler debounced (250ms) to coalesce bursts of
 *       approvals/rejects/clock-events into a single query invalidation
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { scheduleKeys } from '@/hooks/schedule/queryKeys';


interface UseCompanyRealtimeOptions {
  companyId: string | undefined;
  offersEnabled: boolean;
  enabled?: boolean;
}

export function useCompanyRealtimeSubscription({
  companyId,
  offersEnabled,
  enabled = true,
}: UseCompanyRealtimeOptions) {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const isMountedRef = useRef(true);

  // Store mutable config in refs to avoid effect re-runs
  const offersEnabledRef = useRef(offersEnabled);
  offersEnabledRef.current = offersEnabled;

  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  const invalidateOpenShifts = useCallback(() => {
    if (!isMountedRef.current || !companyId) return;
    queryClientRef.current.invalidateQueries({
      queryKey: [...scheduleKeys.all, 'openShifts', companyId],
      exact: false,
    });
  }, [companyId]);

  // F7: debounce timesheet invalidations — bursts of clock events or bulk
  // approvals can fire many UPDATEs in a short window.
  const timesheetDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invalidateTimesheets = useCallback(() => {
    if (!isMountedRef.current || !companyId) return;
    if (timesheetDebounceRef.current) clearTimeout(timesheetDebounceRef.current);
    timesheetDebounceRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      // Prefix-match invalidates both manager (`['timesheets','company',...]`)
      // and self-service (`['timesheets','my',...]`) views.
      queryClientRef.current.invalidateQueries({
        queryKey: ['timesheets'],
        exact: false,
      });
    }, 250);
  }, [companyId]);


  useEffect(() => {
    isMountedRef.current = true;

    if (!companyId || !enabled) return;

    // F3: Cleanup existing channel before creating new one
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channelName = `company-realtime-${companyId}`;

    const channel = supabase
      .channel(channelName)
      // Handler 1: shifts table (for open shifts)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'shifts',
          filter: `company_id=eq.${companyId}`,
        },
        (payload) => {
          if (!isMountedRef.current) return;

          // F5: Only invalidate if offers feature is enabled
          if (!offersEnabledRef.current) return;

          const newRecord = payload.new as Record<string, unknown> | null;
          const oldRecord = payload.old as Record<string, unknown> | null;

          const isOpenShiftChange =
            newRecord?.status === 'open' ||
            oldRecord?.status === 'open' ||
            (payload.eventType === 'UPDATE' &&
              (newRecord?.profile_id === null || oldRecord?.profile_id === null));

          if (isOpenShiftChange) {
            invalidateOpenShifts();
          }
        }
      )
      // Handler 2: time_entries table (for timesheet status changes —
      // approve / reject / revert / bulk create — pushed across devices
      // so managers don't act on stale `pending` cards.)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'time_entries',
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          if (!isMountedRef.current) return;
          invalidateTimesheets();
        }
      )
      .subscribe((status, err) => {
        if (!isMountedRef.current) return;
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          console.warn('[Company Realtime] Connection closed/error:', err);
        }
      });

    channelRef.current = channel;

    // F4: Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      if (timesheetDebounceRef.current) {
        clearTimeout(timesheetDebounceRef.current);
        timesheetDebounceRef.current = null;
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [companyId, enabled, invalidateOpenShifts, invalidateTimesheets]);
}
